import {useClient, useDictionary, useOrder} from '@adamlip/okeoke.client';
import PaymentAgentSelector from 'components/_Payments/PaymentAgentSelector';
import {AppParamsContext} from 'contexts/AppParamsContext';
import useBasket from 'hooks/useBasket';
import {NumericFormat} from 'react-number-format';
import {useParams} from 'react-router-dom';
import {useContext, useEffect, useState} from 'react';
import {UserContext} from 'contexts/UserContext';
import AddCouponModal from 'components/_VIP/AddCouponModal';
import ActivatePointsModal from 'components/_VIP/ActivatePointsModal';
import {Card, CardBody} from '@heroui/react';
import Icon from 'assets/icons';

export default function PaymentTypeSelector({onSelectPayment, usableMoney, payByVipMoney, setPayByVipMoney, paymentModalOpen, setPaymentModalOpen}) {
	const {id} = useParams();
	const {locations, selectedLocationUUID} = useClient();
	const {getValue} = useDictionary();
	const {paymentsNeeded, isReady, orderNumber, totalPrice} = useOrder(null, id);
	const {handleChangePaymentType, selectedPayment, setSelectedPayment} = useBasket();
	const {appParams} = useContext(AppParamsContext);
	const [paymentAgents, setPaymentAgents] = useState([]);
	const {userVipPoints, isVipBrand, reserveLoading, setUsePoints} = useContext(UserContext);

	const [addCouponModalOpen, setAddCouponModalOpen] = useState(false);
	const [activatePointsModalOpen, setActivatePointsModalOpen] = useState(false);
	const handleSelectPaymentAgent = (agent) => {
		handleChangePaymentType('Online');
		setSelectedPayment(agent);
		if (typeof onSelectPayment === 'function') onSelectPayment(agent);
	};

	useEffect(() => {
		if (locations?.[selectedLocationUUID]?.paymentAgents) {
			setPaymentAgents(locations?.[selectedLocationUUID]?.paymentAgents?.filter((agent) => agent.agentType === 'online'));
		} else {
			setPaymentAgents([]);
		}
	}, [locations, selectedLocationUUID]);

	if (!isReady) return null;
	if (isReady && paymentsNeeded === 0) return <div className='text-primay text-xl font-bold'>{getValue('app_v2_orderPayed')}</div>;
	return (
		<div className={`${appParams?.styles?.fonts?.capitalize ? 'font-extrabold uppercase' : ''} flex flex-col grow gap-2 mt-2 h-auto`}>
			<PaymentAgentSelector
				paymentAgents={paymentAgents}
				paymentsNeeded={paymentsNeeded}
				selectedID={selectedPayment?.paymentAgentID}
				selectPaymentAgent={handleSelectPaymentAgent}
				usableMoney={usableMoney}
				payByVipMoney={payByVipMoney}
				setPayByVipMoney={setPayByVipMoney}
				paymentModalOpen={paymentModalOpen}
				setPaymentModalOpen={setPaymentModalOpen}
			/>
			{isVipBrand && (
				<>
					<div className='w-full flex flex-col gap-0 items-start justify-start'>
						{(appParams?.basketModal?.visibleBlocks?.activatePointsOnPayment !== false || appParams?.basketModal?.visibleBlocks?.addCouponCodeOnPayment !== false) && <div className='text-sm opacity-90 '>{getValue('app_v2_basket_vip')}</div>}
						<div className='text-xs opacity-70 '>{getValue('app_v2_basket_vip_desc')}</div>
					</div>

					{appParams?.basketModal?.visibleBlocks?.activatePointsOnPayment !== false && (
						<>
							<Card isPressable={userVipPoints > 99} isDisabled={userVipPoints < 100} onPress={() => setActivatePointsModalOpen(true)}>
								<CardBody className='w-full flex flex-row gap-2 items-center justify-between'>
									<div className='flex flex-row gap-2 items-center'>
										<div className='w-6 h-6'>
											<Icon name='vip' className='stroke-current' />
										</div>
										<div className='flex flex-col gap-0'>
											<div>
												{getValue('app_v2_coupons_activatePoints')}{' '}
												<span className='text-xs'>
													({getValue('app_v2_vip_point_balance')}:{userVipPoints || 0})
												</span>
											</div>
											{+userVipPoints < 100 && <div className='w-full text-xs'>{getValue('app_v2_vip_points_not_enough')}</div>}
										</div>
									</div>
									{userVipPoints > 99 && (
										<div className='w-6 h-6'>
											<Icon name='right' className='stroke-current' />
										</div>
									)}
								</CardBody>
							</Card>
						</>
					)}
					{appParams?.basketModal?.visibleBlocks?.addCouponCodeOnPayment !== false && (
						<>
							<Card isPressable onPress={() => setAddCouponModalOpen(true)}>
								<CardBody className='w-full flex flex-row gap-2 items-center justify-between'>
									<div className='flex flex-row gap-2 items-center'>
										<div className='w-6 h-6'>
											<Icon name='coupon' className='stroke-current' />
										</div>
										<div>{getValue('app_v2_coupons_addCoupon')}</div>
									</div>
									<div className='w-6 h-6'>
										<Icon name='right' className='stroke-current' />
									</div>
								</CardBody>
							</Card>
						</>
					)}
				</>
			)}
			<div className='text-xs opacity-70 '>{getValue('app_v2_basket_price_breakdown')}</div>

			<div className='w-full flex flex-col gap-2 items-start justify-start'>
				{orderNumber != null && (
					<div className='w-full text-center'>
						#{orderNumber} {getValue('app_v2_order')}
					</div>
				)}
				<div className='w-full flex flex-row gap-2 items-center justify-between'>
					<div className='label'>{getValue('app_v2_payment_totalPrice')}:</div>
					<div className='value'>
						<NumericFormat value={+totalPrice} decimalSeparator=',' suffix=' Ft' thousandSeparator='.' displayType='text' />
					</div>
				</div>
				<div className='w-full flex flex-row gap-2 items-center justify-between font-medium'>
					<div className='label'>{getValue('app_v2_payment_paymentsNeeded')}:</div>
					<div className='value'>
						<NumericFormat value={+paymentsNeeded} decimalSeparator=',' suffix=' Ft' thousandSeparator='.' displayType='text' />
					</div>
				</div>
			</div>
			{isVipBrand && (
				<>
					<AddCouponModal isOpen={addCouponModalOpen} setIsOpen={setAddCouponModalOpen} />
					<ActivatePointsModal isOpen={activatePointsModalOpen} setIsOpen={setActivatePointsModalOpen} />
				</>
			)}
		</div>
	);
}
